import { useAuth, useSignIn } from '@clerk/nextjs'
import { Button, Field } from '@components/gassan-ui'
import { Text } from '@components/gassan-ui/Typography/Text'
import InlineNotification from '@components/inline-notification'
import { cn } from '@lib/cn'
import { useClerkErrorMessage } from '@lib/hooks/use-clerk-error-message'
import { useCountdown } from '@lib/hooks/use-countdown'
import { useSignInRedirectUrl } from '@lib/hooks/use-sign-in-redirect-url'
import { passwordConfirmationRequired, passwordRequired } from '@lib/yup'
import { Formik, Form as FormikForm, FormikProps } from 'formik'
import { useTranslation } from 'next-i18next'
import { FC, useState } from 'react'
import * as Yup from 'yup'

const codeSchema = Yup.object().shape({
  code: Yup.string().required('forms.errors.isRequired').length(6, 'forms.errors.codeLength'),
  newPassword: passwordRequired,
  repeatPassword: passwordConfirmationRequired,
})

type CodeFormValues = {
  code: string
  newPassword: string
  repeatPassword: string
}

type ChangePasswordFormProps = {
  onSuccess: () => void
  email: string
  explanation?: string
}

export const ChangePasswordForm: FC<ChangePasswordFormProps> = ({
  onSuccess,
  email,
  explanation,
}) => {
  const { t } = useTranslation(['account', 'forms', 'other'])
  const { isLoaded, signIn, setActive } = useSignIn()
  const { isSignedIn, signOut } = useAuth()
  const getClerkErrorMessage = useClerkErrorMessage()
  const [error, setError] = useState<string | null>(null)
  const [isResending, setIsResending] = useState(false)
  const { count: countdown, hasFinished, start: startCountdown } = useCountdown(30)
  const signInRedirectUrl = useSignInRedirectUrl()

  const handleResendCode = async () => {
    setIsResending(true)
    setError(null)

    try {
      if (!isLoaded) return

      if (isSignedIn) {
        await signOut()
      }

      await signIn.create({
        strategy: 'reset_password_email_code',
        identifier: email,
      })

      startCountdown()
    } catch (_error: unknown) {
      const clerkMessage = getClerkErrorMessage(_error)
      setError(clerkMessage || t('errors.unableToResetPassword', { ns: 'forms' }))
    }

    setIsResending(false)
  }

  return (
    <>
      {explanation && <Text className={cn('px-4 py-0', 'md:mb-6 md:p-0')}>{explanation}</Text>}
      <Text className={cn('px-4 py-0', 'md:mb-6 md:p-0')}>
        {t('enterVerificationCode', { ns: 'account' })}
      </Text>

      <Formik
        validateOnChange
        validateOnBlur
        validateOnMount={false}
        validationSchema={codeSchema}
        initialValues={{ code: '', newPassword: '', repeatPassword: '' }}
        enableReinitialize
        onSubmit={async (values) => {
          setError(null)

          try {
            if (!isLoaded) return

            if (isSignedIn) {
              await signOut()
            }

            const result = await signIn.attemptFirstFactor({
              strategy: 'reset_password_email_code',
              code: values.code,
              password: values.newPassword,
            })

            if (result.status === 'complete') {
              await setActive({ session: result.createdSessionId, redirectUrl: signInRedirectUrl })
              onSuccess()
            }
          } catch (_error: unknown) {
            const clerkMessage = getClerkErrorMessage(_error)
            setError(clerkMessage || t('errors.unableToResetPassword', { ns: 'forms' }))
          }
        }}
      >
        {({ isValid, isSubmitting }: FormikProps<CodeFormValues>) => {
          return (
            <FormikForm className="px-4 md:px-0">
              <Field
                field="input"
                name="code"
                label={t('labels.verificationCode', { ns: 'forms' })}
                placeholder="000000"
                maxLength={6}
                autoFocus
              />

              <Field
                type="password"
                field="input"
                name="newPassword"
                label={t('labels.password', { ns: 'forms' })}
              />

              <Field
                type="password"
                field="input"
                name="repeatPassword"
                label={t('labels.repeatNewPassword', { ns: 'forms' })}
                assistiveText={t('atLeast8CharactersExtended', { ns: 'other' })}
              />

              {error && <InlineNotification variant="error" title={error} />}

              <div className="mt-6 flex flex-col gap-4 md:mt-8">
                <Button
                  type="submit"
                  variant="dark"
                  className="w-full"
                  status={isSubmitting ? 'loading' : isValid ? 'idle' : 'disabled'}
                >
                  {t('resetMyPassword', { ns: 'other' })}
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  status={isResending ? 'loading' : hasFinished ? 'idle' : 'disabled'}
                  onClick={handleResendCode}
                  disabled={!hasFinished}
                >
                  {hasFinished
                    ? t('resendCode', { ns: 'account' })
                    : t('resendCodeIn', { ns: 'account', count: countdown })}
                </Button>
              </div>
            </FormikForm>
          )
        }}
      </Formik>
    </>
  )
}
